import React, { createContext, useContext, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useCurrentFirm } from '../../hooks/queries/useLawFirms';

const AttorneyContext = createContext(null);

/**
 * Attorney Context Provider
 * Provides attorney-specific state and permissions.
 *
 * Access and permissions are determined by the law_firms table lookup,
 * which allows both internal (MarkoTM) and external law firm attorneys.
 */
export function AttorneyProvider({ children }) {
  const { user } = useUser();
  const [selectedClient, setSelectedClient] = useState(null);

  // Get firm and permissions from API (already fetched by AttorneyApp, will use cache)
  const { data: firmData } = useCurrentFirm();

  // Extract values from API response
  const firm = firmData?.firm || null;
  const permissions = firmData?.permissions || {};
  const role = firmData?.role || null;

  // Role checks from API
  const isAdmin = permissions.isAdmin || false;
  const isAttorneyAdmin = permissions.isAttorneyAdmin || false;
  const isAttorney = firmData?.hasAccess || false;
  const isInternal = firm?.isInternal || false;

  // Permission checks from API
  const canManageJurisdictions = isAdmin;
  const canManageAssignments = permissions.canManageAssignments || false;
  const canTransferToFirm = permissions.canTransferToFirm || false;
  const canRecallFromFirm = permissions.canRecallFromFirm || false;
  const canReassign = permissions.canReassign || false;

  // Messaging permissions - only MarkoTM (internal) can message clients directly
  const canMessageClients = isInternal;
  const canCreateInternalRequests = !isInternal; // External attorneys use internal requests
  const canProcessInternalRequests = isInternal && (isAdmin || isAttorneyAdmin);

  // User metadata from Clerk
  const specializations = user?.publicMetadata?.specializations || [];
  const assignedClients = user?.publicMetadata?.assigned_clients || [];

  // canAccessClient - check against firm's access level
  const canAccessClient = useCallback((clientUserId) => {
    // Full access firms can see all
    if (firm?.accessLevel === 'full') return true;
    // Admin and attorneyadmin can access all
    if (isAdmin || isAttorneyAdmin) return true;
    // Otherwise check assigned list
    return assignedClients.includes(clientUserId);
  }, [firm?.accessLevel, isAdmin, isAttorneyAdmin, assignedClients]);

  const value = {
    // Role checks
    isAdmin,
    isAttorneyAdmin,
    isAttorney,
    isInternal,
    memberRole: role,

    // Permission checks
    canAccessClient,
    canManageJurisdictions,
    canManageAssignments,
    canTransferToFirm,
    canRecallFromFirm,
    canReassign,

    // Messaging permissions
    canMessageClients,
    canCreateInternalRequests,
    canProcessInternalRequests,

    // User data
    specializations,
    assignedClients,
    attorneyName: user?.fullName || user?.firstName || 'Attorney',
    attorneyId: user?.id,

    // Firm data
    firm,
    firmName: firm?.firmName || 'Law Firm',
    accessLevel: firm?.accessLevel || 'assigned_only',

    // UI state
    selectedClient,
    setSelectedClient,

    // Legacy (for backwards compatibility)
    organizationName: firm?.firmName || 'Law Firm',
    isMarkoTM: isInternal // Legacy alias
  };

  return (
    <AttorneyContext.Provider value={value}>
      {children}
    </AttorneyContext.Provider>
  );
}

export function useAttorney() {
  const context = useContext(AttorneyContext);
  if (!context) {
    throw new Error('useAttorney must be used within an AttorneyProvider');
  }
  return context;
}

export default AttorneyContext;
